import { useQuery } from '@apollo/client';
import { EmployeeAvatar } from '@newfront-insurance/core-ui';
import { X } from 'lucide-react';
import type { ClipboardEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import {
  getEmailAddresses,
  getEmployeeDisplayName,
  getEmployeeInitials,
  type ShareableEmployee,
} from './employee-utils';
import { SEARCH_EMPLOYEES } from '../../../../../queries/chats';

const SHARE_USER_RESULT_LIMIT = 8;

export function SkillShareUserPicker({
  excludedUserUuids,
  inputId,
  selectedEmployees,
  onAddEmployee,
  onRemoveEmployee,
}: {
  excludedUserUuids: Set<string>;
  inputId?: string;
  selectedEmployees: ShareableEmployee[];
  onAddEmployee: (employee: ShareableEmployee) => void;
  onRemoveEmployee: (userUuid: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingPastedEmails, setPendingPastedEmails] = useState<string[]>([]);
  useDebounce(() => setSearchQuery(query.trim()), 200, [query]);
  const { data: employeeSearchData, loading: isEmployeeSearchLoading } = useQuery(SEARCH_EMPLOYEES, {
    variables: { query: searchQuery, limit: SHARE_USER_RESULT_LIMIT },
    skip: !searchQuery,
    fetchPolicy: 'network-only',
  });
  const matchingEmployees = useMemo<ShareableEmployee[]>(
    () =>
      (employeeSearchData?.employees ?? []).map((employee) => ({
        userUuid: employee.userUuid,
        email: employee.email,
        name: employee.fullName,
        imageUrl: employee.imageUrl ?? undefined,
        firstName: employee.firstName ?? undefined,
        lastName: employee.lastName ?? undefined,
        title: employee.title ?? undefined,
        status: 'active',
      })),
    [employeeSearchData?.employees],
  );
  const selectedUserUuids = useMemo(
    () => new Set(selectedEmployees.map((employee) => employee.userUuid)),
    [selectedEmployees],
  );
  const visibleEmployees = useMemo(() => {
    return matchingEmployees
      .filter((employee) => !excludedUserUuids.has(employee.userUuid) && !selectedUserUuids.has(employee.userUuid))
      .slice(0, SHARE_USER_RESULT_LIMIT);
  }, [excludedUserUuids, matchingEmployees, selectedUserUuids]);

  useEffect(() => {
    if (pendingPastedEmails.length === 0 || isEmployeeSearchLoading) {
      return;
    }

    const pendingEmailSet = new Set(pendingPastedEmails);
    const resolvedEmployees = matchingEmployees.filter(
      (employee) => employee.email && pendingEmailSet.has(employee.email.toLowerCase()),
    );

    if (resolvedEmployees.length === 0) {
      return;
    }

    const resolvedEmailSet = new Set<string>();
    for (const employee of resolvedEmployees) {
      const normalizedEmail = employee.email.toLowerCase();
      resolvedEmailSet.add(normalizedEmail);

      if (!excludedUserUuids.has(employee.userUuid) && !selectedUserUuids.has(employee.userUuid)) {
        onAddEmployee(employee);
      }
    }

    const remainingEmails = pendingPastedEmails.filter((email) => !resolvedEmailSet.has(email));
    setPendingPastedEmails(remainingEmails);
    setQuery(remainingEmails.join(', '));
    setIsOpen(remainingEmails.length > 0);
  }, [
    excludedUserUuids,
    isEmployeeSearchLoading,
    matchingEmployees,
    onAddEmployee,
    pendingPastedEmails,
    selectedUserUuids,
  ]);

  const handleSearchChange = (nextQuery: string) => {
    setQuery(nextQuery);
    setPendingPastedEmails([]);
    setIsOpen(true);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedEmails = getEmailAddresses(event.clipboardData.getData('text'));

    if (pastedEmails.length === 0) {
      return;
    }

    event.preventDefault();
    setPendingPastedEmails(pastedEmails);
    setQuery(pastedEmails.join(', '));
    setIsOpen(true);
  };

  const handleSelectEmployee = (employee: ShareableEmployee) => {
    onAddEmployee(employee);
    setQuery('');
    setPendingPastedEmails([]);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <div
        className={[
          'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-[4px] border border-[#BEC7D0] bg-white',
          'px-2 py-1.5 focus-within:border-[#0957C3]',
        ].join(' ')}
      >
        {selectedEmployees.map((employee) => {
          const employeeName = getEmployeeDisplayName(employee);

          return (
            <span
              key={employee.userUuid}
              className="inline-flex h-7 max-w-full items-center gap-1 rounded-[4px] bg-[#E2EBF1] px-2 font-['Open_Sans',sans-serif] text-[13px] leading-5 font-semibold text-[#262D46]"
            >
              <span className="max-w-[132px] truncate">{employeeName}</span>
              <button
                type="button"
                aria-label={`Remove ${employeeName}`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#546A83] hover:bg-[#D5E0EA] hover:text-[#262D46]"
                onClick={() => onRemoveEmployee(employee.userUuid)}
              >
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        <input
          id={inputId}
          role="combobox"
          aria-label="Search people by name or email"
          aria-expanded={isOpen}
          aria-controls={isOpen ? 'custom-skill-share-user-results' : undefined}
          value={query}
          onBlur={() => window.setTimeout(() => setIsOpen(false), 150)}
          onChange={(event) => handleSearchChange(event.target.value)}
          onFocus={() => setIsOpen(true)}
          onPaste={handlePaste}
          placeholder={selectedEmployees.length > 0 ? '' : 'Search by name or email'}
          className="h-7 min-w-[140px] flex-1 border-0 bg-transparent px-1 py-0 font-['Open_Sans',sans-serif] text-[14px] leading-5 font-normal text-[#262D46] outline-none placeholder:text-[#98A6B5]"
        />
      </div>

      {isOpen ? (
        <div
          id="custom-skill-share-user-results"
          role="listbox"
          className="absolute top-[calc(100%+4px)] right-0 left-0 z-10 max-h-64 overflow-auto rounded-[4px] border border-[#DEE2E7] bg-white py-1 shadow-lg"
        >
          {isEmployeeSearchLoading ? (
            <p className="px-3 py-3 text-sm text-[#546A83]">Searching people...</p>
          ) : !searchQuery ? (
            <p className="px-3 py-3 text-sm text-[#546A83]">Start typing a name or email.</p>
          ) : visibleEmployees.length > 0 ? (
            visibleEmployees.map((employee) => {
              const employeeName = getEmployeeDisplayName(employee);

              return (
                <button
                  key={employee.userUuid}
                  type="button"
                  role="option"
                  aria-selected={false}
                  aria-label={`Select ${employeeName}${employee.email ? ` ${employee.email}` : ''}`}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[#F4F8FE]"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelectEmployee(employee)}
                >
                  <EmployeeAvatar
                    sizeInPixels={32}
                    imageUrl={employee.imageUrl}
                    initials={getEmployeeInitials(employee, employee.userUuid)}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-[#262D46]">{employeeName}</span>
                    {employee.email ? (
                      <span className="block truncate text-xs text-[#546A83]">{employee.email}</span>
                    ) : null}
                  </span>
                </button>
              );
            })
          ) : (
            <p className="px-3 py-3 text-sm text-[#546A83]">No people found.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
